/**
 * @typedef {Object} Column
 * @property {string} field - The field identifier
 * @property {string} header - Column header text
 * @property {string} align - Alignment of the column content
 * @property {string} [type] - Type of the column (e.g., 'date-time')
 * @property {string} [columnFilter] - Type of filter ('text', 'date', 'multiple')
 * @property {string} [filterName] - Name of the filter
 * @property {boolean} [filter] - Whether the column is filterable
 * @property {Array<Object>} [filters] - Filter options for multiple-type filters
 * @property {string} [left] - Left alignment override
 */
import {GetTranslationNameFromArray} from '../../../../infra/translations/AvailableTranslations'
import {CustomStockHistoryModalColumnWrapper} from './components/CustomStockHistoryModalColumnWrapper'

/**
 * @typedef {Object} TableConfig
 * @property {Array<Column>} columns - Array of column configurations
 * @property {Array<Object>} data - Table data rows
 * @property {number} size - Page size for pagination
 * @property {Object} scroll - Scrolling configuration
 * @property {boolean} scroll.scrollable - Whether table is scrollable
 * @property {string} scroll.scrollableHeight - Height of scrollable area
 * @property {boolean} footer - Whether to show table footer
 * @property {Object} editrow - Edit row configuration
 * @property {boolean} editrow.hasEdit - Whether rows are editable
 * @property {Object} checkboxes - Checkbox configuration
 * @property {boolean} checkboxes.checked - Whether checkboxes are checked
 */

/**
 * Renders the column configuration for the stock history modal
 * @param {Array<Object>} movementTypes - Available movement types for filtering
 * @param {Array<Object>} warehouses - Available warehouses for filtering
 * @param {Array<Object>} user - Available users for filtering
 * @param {Object} data - Table data with rows property
 * @param {number} pageSize - Number of rows per page
 * @returns {TableConfig} Complete table configuration object
 */

// FIXME change to activeLanguage once active language is set up
const LANGUAGE_ID = 2

export const createStockHistoryModalColumns = (
  movementTypes,
  warehouses,
  usersWithMovements,
  data,
  pageSize
) => {
  const movementTypesDropdownData = movementTypes?.items?.map(item => ({
    value: item?.movementTypeId,
    label: GetTranslationNameFromArray(
      item?.movementTypeTranslation,
      LANGUAGE_ID
    )
  }))

  const warehousesDropdownData = warehouses?.items?.map(item => ({
    value: item?.warehouseId,
    label: item?.contactName
  }))

  const usersWithMovementsDropdownData = usersWithMovements?.items?.map(
    item => ({
      value: item?.userId,
      label: item?.fullName
    })
  )

  return {
    columns: [
      {
        field: 'pcwMovementId',
        header: 'ID',
        align: 'space-between',
        columnFilter: 'text',
        filterName: 'pcwMovementId',
        filter: true
      },
      {
        type: 'date-time',
        field: 'date',
        header: 'Date',
        align: 'space-between',
        columnFilter: 'date',
        filterName: 'date',
        filter: true
      },
      {
        field: 'movementTypeName',
        header: 'Movement Type',
        align: 'space-between',
        columnFilter: 'multiple',
        filterName: 'movementType',
        type: 'custom',
        filter: true,
        filters: movementTypesDropdownData,
        body: rowData => {
          return (
            <CustomStockHistoryModalColumnWrapper>
              {GetTranslationNameFromArray(
                rowData.movementType.movementTypeTranslation
              )}
            </CustomStockHistoryModalColumnWrapper>
          )
        }
      },
      {
        field: 'warehouseName',
        header: 'Warehouse',
        align: 'space-between',
        columnFilter: 'multiple',
        filterName: 'warehouseType',
        filter: true,
        filters: warehousesDropdownData
      },
      {
        field: 'initialStock',
        header: 'Init stock',
        align: 'center',
        type: 'custom',
        body: rowData => {
          const initialStock = rowData.resultingStock - rowData.quantity

          return (
            <CustomStockHistoryModalColumnWrapper align="center">
              {initialStock}
            </CustomStockHistoryModalColumnWrapper>
          )
        }
      },
      {
        field: 'quantity',
        header: 'Qty',
        align: 'center'
      },
      {
        field: 'resultingStock',
        header: 'Final stock',
        align: 'center'
      },
      {
        field: 'createdBy.fullName',
        header: 'User',
        align: 'space-between',
        columnFilter: 'multiple',
        filterName: 'users',
        filter: true,
        filters: usersWithMovementsDropdownData,
        left: 'left',
        type: 'custom',
        body: rowData => {
          return (
            <CustomStockHistoryModalColumnWrapper>
              {rowData.createdBy.fullName}
            </CustomStockHistoryModalColumnWrapper>
          )
        }
      }
    ],
    data: data?.rows,
    size: pageSize,
    scroll: {
      scrollable: true,
      scrollableHeight: `${window.innerHeight - 360}px`
    },
    footer: false,
    editrow: {
      hasEdit: false
    },
    checkboxes: {
      checked: false
    }
  }
}
